import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../services/supabase';
import Pagination from '../Pagination';
import { scrollToResultsIfStacked } from '../../utils/scrollToResults';
import './UserSearch.css';

/** Artists shown per search results page */
const PAGE_SIZE = 9;

/** Roles included on Find Artists (listeners excluded) */
const ARTIST_ROLES = ['producer', 'rapper', 'producer_rapper'] as const;

interface UserSearchResult {
  id: string;
  username: string;
  role: (typeof ARTIST_ROLES)[number];
  state: string | null;
}

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
];

type RoleFilterValue = '' | 'producer' | 'rapper' | 'producer_rapper';

const ROLE_OPTIONS: { value: RoleFilterValue; label: string }[] = [
  { value: '', label: 'All Roles (Only artists)' },
  { value: 'producer', label: 'Producer' },
  { value: 'rapper', label: 'Rapper' },
  { value: 'producer_rapper', label: 'Producer & Rapper' },
];

function formatRoleLabel(role: UserSearchResult['role']): string {
  if (role === 'producer_rapper') return 'Producer & Rapper';
  if (role === 'producer') return 'Producer';
  if (role === 'rapper') return 'Rapper';
  return role;
}

function roleBadgeClass(role: UserSearchResult['role']): string {
  if (role === 'producer') return 'search-page__badge--role-producer';
  if (role === 'rapper') return 'search-page__badge--role-rapper';
  return 'search-page__badge--role';
}

const UserSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleFilterValue>('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [heroReady, setHeroReady] = useState(false);
  const resultsRef = useRef<HTMLElement | null>(null);
  const filtersTouchedRef = useRef(false);
  const pendingScrollRef = useRef(false);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Mark that the user changed a filter (skip the initial mount). Name
  // search is included but scrolling waits until the debounced fetch
  // finishes, so we don't jump on every keystroke.
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    pendingScrollRef.current = true;
  }, [searchTerm, selectedState, selectedRole]);

  // On mobile/tablet the filters sit above the results — scroll only after
  // a load cycle finishes (covers the 300ms search debounce).
  useEffect(() => {
    if (loading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current || !pendingScrollRef.current) return;
    wasLoadingRef.current = false;
    pendingScrollRef.current = false;
    return scrollToResultsIfStacked(resultsRef.current);
  }, [loading]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedState, selectedRole]);

  useEffect(() => {
    const searchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const from = (page - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
          .from('profiles')
          .select('id, username, role, state', { count: 'exact' });

        const term = searchTerm.trim();
        if (term) {
          query = query.ilike('username', `%${term}%`);
        }
        if (selectedState) {
          query = query.eq('state', selectedState);
        }
        if (selectedRole) {
          query = query.eq('role', selectedRole);
        } else {
          query = query.in('role', [...ARTIST_ROLES]);
        }

        const { data, error: searchError, count } = await query
          .order('username')
          .range(from, to);

        if (searchError) throw searchError;
        setResults(data || []);
        setTotalCount(count ?? 0);
      } catch (err) {
        console.error('Search error:', err);
        setError('Error performing search. Please try again.');
        setResults([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedState, selectedRole, page]);

  const renderResults = () => {
    if (loading) {
      return (
        <div className="search-page__loading">
          <div className="search-page__spinner" aria-label="Loading" />
          <p>Loading...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="search-page__error" role="alert">
          <p>{error}</p>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className="search-page__empty">
          <span className="search-page__empty-icon" aria-hidden>
            🔍
          </span>
          <p>No users found. Try adjusting your search criteria.</p>
        </div>
      );
    }

    return (
      <>
        <ul className="search-page__results-grid">
          {results.map((user, i) => (
            <li key={user.id}>
              <Link
                to={`/profile/${user.id}`}
                className="search-page__artist-card"
                style={{ '--card-i': i } as React.CSSProperties}
              >
                <div className="search-page__avatar">{user.username.charAt(0).toUpperCase()}</div>
                <div className="search-page__artist-info">
                  <span className="search-page__artist-name">{user.username}</span>
                  <div className="search-page__artist-meta">
                    <span className={`search-page__badge search-page__badge--role ${roleBadgeClass(user.role)}`}>
                      {formatRoleLabel(user.role)}
                    </span>
                    <span className="search-page__badge search-page__badge--state">
                      {user.state || 'No state'}
                    </span>
                  </div>
                </div>
                <svg className="search-page__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
        <div className="search-page__pagination">
          <Pagination page={page} setPage={setPage} total={totalCount} pageSize={PAGE_SIZE} />
        </div>
      </>
    );
  };

  return (
    <div className="search-page">
      <Helmet>
        <title>DHH Collab | Find Artists</title>
        <meta
          name="description"
          content="Search producers and rappers across India by name, state, and role on DHH Collab."
        />
      </Helmet>

      <div className="search-page__bg" aria-hidden>
        <span className="search-page__blob search-page__blob--1" />
        <span className="search-page__blob search-page__blob--2" />
        <span className="search-page__grid-pattern" />
      </div>

      <section className={`search-page__hero ${heroReady ? 'search-page__hero--visible' : ''}`}>
        <div className="search-page__hero-inner search-page__content-wrap container-padding">
          <div>
            <span className="search-page__eyebrow">Community</span>
            <h1 className="search-page__title">Find Artists</h1>
            <p className="search-page__subtitle">
              Discover producers and rappers across India — search by name, state, or role.
            </p>
          </div>

          <div className="search-page__network" aria-hidden>
            <svg className="search-page__network-svg" viewBox="0 0 220 120">
              <line className="search-page__network-line" x1="110" y1="60" x2="40" y2="28" />
              <line className="search-page__network-line" x1="110" y1="60" x2="180" y2="32" />
              <line className="search-page__network-line" x1="110" y1="60" x2="55" y2="95" />
              <line className="search-page__network-line" x1="110" y1="60" x2="165" y2="88" />
              <line className="search-page__network-line" x1="40" y1="28" x2="180" y2="32" />
              <circle className="search-page__network-pulse" cx="110" cy="60" r="8" />
              <circle className="search-page__network-node" cx="40" cy="28" r="5" />
              <circle className="search-page__network-node search-page__network-node--b" cx="180" cy="32" r="5" />
              <circle className="search-page__network-node search-page__network-node--c" cx="55" cy="95" r="4" />
              <circle className="search-page__network-node" cx="165" cy="88" r="4" />
            </svg>
          </div>
        </div>
      </section>

      <div className="search-page__body search-page__content-wrap container-padding">
        <div className="search-page__layout">
          <aside className="search-page__sidebar" aria-label="Search filters">
            <div className="search-page__sidebar-card">
              <div className="search-page__sidebar-head">
                <h2 className="search-page__sidebar-title">Search</h2>
                <p className="search-page__sidebar-hint">Combine filters to narrow your results.</p>
              </div>

              <p className="search-page__filters-heading">Filters</p>

              <div className="search-page__field">
                <label htmlFor="search" className="search-page__label">
                  Search by Name
                </label>
                <div className="search-page__input-wrap">
                  <svg className="search-page__input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Enter name..."
                    className="search-page__input"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="search-page__field">
                <label htmlFor="state" className="search-page__label">
                  Filter by State
                </label>
                <select
                  id="state"
                  value={selectedState}
                  onChange={e => setSelectedState(e.target.value)}
                  className="search-page__select"
                >
                  <option value="">All States</option>
                  {STATES.map(state => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div className="search-page__field">
                <span className="search-page__label" id="role-filter-label">
                  Filter by Role
                </span>
                <div className="search-page__role-pills" role="group" aria-labelledby="role-filter-label">
                  {ROLE_OPTIONS.map(opt => (
                    <button
                      key={opt.value || 'all-artist-roles'}
                      type="button"
                      className={`search-page__role-pill ${selectedRole === opt.value ? 'search-page__role-pill--active' : ''}`}
                      onClick={() => setSelectedRole(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <main className="search-page__main" ref={resultsRef}>
            <header className="search-page__results-header">
              <h2 className="search-page__results-title">Artists</h2>
              {!loading && !error && (
                <p className="search-page__results-count" aria-live="polite">
                  <strong>{totalCount}</strong> found
                </p>
              )}
            </header>
            <div className="search-page__panel">{renderResults()}</div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserSearch;
